import { useMemo } from "react";
import type { ExternalReference } from "./build-line-item";
import { derivePaymentStatus, type PaymentStatus } from "./payment-status";
import { usePreServiceInvoices } from "./use-pre-service-invoices";

export interface UsePaymentStatusArgs {
  patientId: string;
  externalReference: ExternalReference | null;
}

/**
 * Reactively derives a single service-point order's payment status. Returns
 * null outside PRE_SERVICE mode or when no invoice carries the order's item —
 * i.e. there is no badge to show and no gate to apply.
 */
export const usePaymentStatus = ({
  patientId,
  externalReference,
}: UsePaymentStatusArgs): PaymentStatus | null => {
  const invoices = usePreServiceInvoices(patientId);
  const origin = externalReference?.origin;
  const foreignId = externalReference?.foreignId;

  return useMemo(
    () =>
      origin && foreignId
        ? derivePaymentStatus(invoices, { origin, foreignId })
        : null,
    [invoices, origin, foreignId],
  );
};
